/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Form {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section, Loader, Button } from "@bw/bits"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { Formik, Form as FormWrap } from "formik"
import { toast } from "react-toastify"
import {
  displayField,
  postFormFields,
  getFormFields,
  getInitialValues,
  fieldsSchema,
} from "./form-actions"
import styled from "styled-components"

const Form = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  backgroundVariant,
  form,
}) => {
  const [formIsSent, setFormIsSent] = React.useState(false)
  const { t } = useTranslation()

  const {
    data: fields,
    isLoading,
    isError,
    isSuccess,
  } = useQuery(["form", form.handle], () => {
    return getFormFields(form.handle)
  })

  if (isError) {
    return null
  }

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        backgroundVariant,
      }}
      containerSize="small"
    >
      {isLoading && (
        <Loader
          messages={[t("loading"), t("loadingTakesLonger")]}
          treshold={5}
        />
      )}

      {isSuccess && formIsSent === false && (
        <Formik
          initialValues={getInitialValues(fields)}
          validationSchema={yup.object().shape({
            ...fieldsSchema(fields, t),
          })}
          onSubmit={values => {
            postFormFields(form.handle, values)
              .then(() => {
                setFormIsSent(true)
              })
              .catch(() => {
                toast.error(t("form.sentFail"))
              })
          }}
        >
          {formik => (
            <FormWrap>
              <FieldList>
                {Object.values(fields).map(field => (
                  <FieldWidth key={field.handle} percent={field.width}>
                    {displayField(field, formik)}
                  </FieldWidth>
                ))}
              </FieldList>
              <Button submit label={t("form.buttonSubmit")} />
            </FormWrap>
          )}
        </Formik>
      )}
      {formIsSent && <div>{t("form.sentSuccess")}</div>}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on FormModule {
 *   ...FormModuleFragment
 * }
 *
 * This is also what you will get in the props of your Form
 * resolver component.
 */
export const query = graphql`
  fragment FormModuleFragment on FormModule {
    form {
      api_url
      handle
    }
  }
`

export { Form }

const FieldWidth = styled.div`
  @media (min-width: 768px) {
    flex: 0 0 calc(${props => props.percent || 100}% - 20px);
  }
`

const FieldList = styled.div`
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 20px;
  }
`
